import type { Easing } from 'motion/react';
import { motionTokens } from '@monksflow/monks-ui/preset';

export const eases = {
  sineIn: [0.12, 0, 0.39, 0],
  sineOut: [0.61, 1, 0.88, 1],
  sineInOut: [0.37, 0, 0.63, 1],

  quadIn: [0.11, 0, 0.5, 0],
  quadOut: [0.5, 1, 0.89, 1],
  quadInOut: [0.45, 0, 0.55, 1],

  cubicIn: [0.32, 0, 0.67, 0],
  cubicOut: [0.33, 1, 0.68, 1],
  cubicInOut: [0.65, 0, 0.35, 1],

  quartIn: [0.5, 0, 0.75, 0],
  quartOut: [0.25, 1, 0.5, 1],
  quartInOut: [0.76, 0, 0.24, 1],

  quintIn: [0.64, 0, 0.78, 0],
  quintOut: [0.22, 1, 0.36, 1],
  quintInOut: [0.83, 0, 0.17, 1],

  circIn: [0.55, 0, 1, 0.45],
  circOut: [0, 0.55, 0.45, 1],
  circInOut: [0.85, 0, 0.15, 1],

  expoIn: [0.7, 0, 0.84, 0],
  expoOut: [0.16, 1, 0.3, 1],
  expoInOut: [0.87, 0, 0.13, 1],
} satisfies Record<string, Easing>;

/**
 * Tessra's duration scale, in seconds.
 *
 * The design system publishes these as CSS custom properties, which JS motion
 * cannot read — so this mirrors them for `motion/react`. Anything animating in
 * JS picks a rung here rather than inventing a number.
 */
export const durations = {
  instant: 0,
  fast: 0.15,
  base: 0.2,
  slow: 0.3,
  slower: 0.5,
} as const;

/**
 * A panel sliding in from or out to an edge, timed as monks-ui's Sheet: in on
 * `duration-slow` with `ease-out`, out on `duration-base` with `ease-exit`.
 */
export const edgePanelTransition = {
  enter: { duration: durations.slow, ease: motionTokens.ease.out },
  exit: { duration: durations.base, ease: motionTokens.ease.exit },
} as const;

/**
 * A small control that takes a panel's place, timed as monks-ui's small overlays: in on
 * `duration-base` with `ease-out`, out on `duration-fast` with `ease-exit`. Its entrance
 * runs as long as an edge panel's exit, so a control and the panel it replaces land together.
 */
export const smallOverlayTransition = {
  enter: { duration: durations.base, ease: motionTokens.ease.out },
  exit: { duration: durations.fast, ease: motionTokens.ease.exit },
} as const;

/**
 * Staggered list-entrance tokens.
 *
 * Drives the "results animate in" effect shared by searchable grids and
 * tables. Each item enters slightly after the previous one; the per-item
 * offset is capped so a large result set doesn't accumulate a long, sluggish
 * cascade. Mirrors the values already proven on the folder subrow reveal:
 * 25ms steps, capped at 8.
 */
export const listStagger = {
  /** Seconds between consecutive items. */
  stepS: 0.025,
  /** Max number of steps before the delay plateaus. */
  maxSteps: 8,
  /** Entrance duration for a single item. */
  durationS: durations.slow,
} as const;

/** Staggered entrance delay (seconds) for the item at `index`, capped. */
export function getStaggerDelay(index: number): number {
  return Math.min(index, listStagger.maxSteps) * listStagger.stepS;
}

/**
 * Page-shell entrance: a header above a content surface.
 *
 * The header arrives from the left and the surface rises under it a beat
 * later, so the eye lands on the header before the content it frames. Read
 * these through `usePageEntrance`, which pairs them with reduced motion.
 */
export const pageEntrance = {
  durationS: durations.slow,
  header: { delayS: 0.1, offsetX: -20 },
  body: { delayS: 0.2, offsetY: 10 },
} as const;

/**
 * Navigation transition tokens.
 *
 * Drives the workspace content-swap animation when the user changes the
 * active top-level section via the left sidebar. The sidebar stays fixed;
 * only the routed content animates so the workspace feels cohesive rather
 * than like a full page reload.
 *
 * Spec: fade in 150ms + translate Y from 8px → 0px, ease-out curve.
 */
export const navContentTransition = {
  duration: durations.fast,
  ease: 'easeOut',
  /** Initial vertical offset (px) the content rises from. */
  offsetY: 8,
} as const;

/**
 * Shared-element transition tokens for the Folders list → Folder detail
 * navigation. Used for the content crossfade and the
 * `layoutId` header morph so both halves stay in lockstep.
 */
export const sharedElementTransition = {
  /** Crossfade between the outgoing and incoming content area. */
  crossfadeDuration: durations.base,
  /** Spring used by the morphing header so the title slides + scales. */
  headerSpring: { type: 'spring', stiffness: 500, damping: 40 } as const,
  /** Brief highlight on the selected row before the content swaps. */
  rowHighlightDuration: durations.slow,
} as const;

/**
 * Per-asset shared-element morph id.
 *
 * Bridges the grid thumbnail (`FileAssetCard`) and the opened viewer image
 * (`ImageOverlay`) so motion morphs the clicked thumbnail INTO the
 * viewer. The id is keyed by `fileId` so only the clicked card matches its
 * viewer counterpart — a shared constant id would make every visible thumbnail
 * compete for the same morph target.
 */
export const getAssetThumbLayoutId = (fileId: string) => `asset-thumb-${fileId}`;

/**
 * Asset viewer animation tokens.
 *
 * Drives the "premium asset review" experience: the thumbnail expands into the
 * viewer (`morphSpring`), the surrounding chrome fades in (`chromeFade`), and
 * next/previous navigation glides the preview horizontally (`glideOffset` +
 * `glideDuration`, eased with `eases.expoOut`) while the side panels stay
 * fixed. All effects are suppressed under `prefers-reduced-motion`.
 */
export const assetViewerTransition = {
  /** Thumbnail → viewer morph. Mirrors `sharedElementTransition.headerSpring`. */
  morphSpring: { type: 'spring', stiffness: 500, damping: 40 } as const,
  /** Chrome (header / filmstrip / comments) fade-in on open. */
  chromeFade: { duration: durations.slow, ease: 'easeOut' } as const,
  /** Horizontal glide offset (px) for next/previous navigation. */
  glideOffset: 48,
  /** Glide enter/exit duration (seconds). Pair with `eases.expoOut`. */
  glideDuration: durations.slow,
} as const;

export const sidebarPanelSpring = {
  type: 'spring',
  stiffness: 280,
  damping: 32,
  mass: 0.8,
} as const;
